import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { supabase } from './api/supabaseclient';
import { LanguageProvider } from './context/LanguageContext';
import { PWAProvider } from './context/PWAContext';
import PageLoader from './components/common/PageLoader';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from "@vercel/speed-insights/react";
import { getSubdomainOrCustomDomain } from './utils/url';

// Lazy Load Pages to optimize bundle size
const SuperAdminPanel = React.lazy(() => import('./pages/SuperAdminPanel'));
const GuestMenu = React.lazy(() => import('./pages/GuestMenu'));
const CafeCover = React.lazy(() => import('./pages/CafeCover'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const OwnerPanel = React.lazy(() => import('./pages/OwnerPanel'));
const YmlFeed = React.lazy(() => import('./pages/YmlFeed'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const CookieConsent = React.lazy(() => import('./components/common/CookieConsent'));
const WaiterApp = React.lazy(() => import('./pages/WaiterApp'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const PublicOffer = React.lazy(() => import('./pages/PublicOffer'));
const UserAgreement = React.lazy(() => import('./pages/UserAgreement'));

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // Data is fresh for 5 minutes
            cacheTime: 1000 * 60 * 30, // Cache persists for 30 minutes
            refetchOnWindowFocus: false, // Don't refetch on window focus to save bandwidth
            retry: 1,
        },
    },
});

const getInitialTheme = () => {
    const savedOwnerTheme = localStorage.getItem('owner_theme');
    const savedSATheme = localStorage.getItem('sa_theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    const path = window.location.pathname;
    const isGuestRoute = path !== '/' && !path.startsWith('/admin') && !path.startsWith('/login') && !path.startsWith('/yml');

    if (isGuestRoute) return 'light';
    if (savedOwnerTheme) return savedOwnerTheme;
    if (savedSATheme) return savedSATheme;
    if (systemPrefersDark) return 'dark';
    return 'light';
};

function App() {
    const [session, setSession] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [userCafeId, setUserCafeId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [splashTheme] = useState(getInitialTheme());
    const [subdomainSlug] = useState(getSubdomainOrCustomDomain());


    useEffect(() => {
        // Intercept OAuth redirects (success or error) to restore the correct menu URL
        const returnPath = localStorage.getItem('oauth_return_path');
        console.log('[Auth] App mounted. Checked returnPath:', returnPath, 'Current URL:', window.location.href);

        if (returnPath) {
            console.log('[Auth] Found returnPath! Redirecting to:', returnPath);
            localStorage.removeItem('oauth_return_path');
            if (returnPath !== window.location.pathname) {
                window.location.replace(returnPath + window.location.search + window.location.hash);
                return;
            }
        }

        let currentUserRoleFetchId = null;

        // 1. Check active session
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            if (session && session.user.id !== currentUserRoleFetchId) {
                currentUserRoleFetchId = session.user.id;
                fetchUserRole(session.user.id);
            } else if (!session) {
                setLoading(false);
            }
        });

        // 2. Listen for changes (login/logout)
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            
            if (_event === 'SIGNED_OUT') {
                currentUserRoleFetchId = null;
                setUserRole(null);
                setUserCafeId(null);
                setLoading(false);
            } else if (session && session.user.id !== currentUserRoleFetchId) {
                // Only fetch if the user ID changed (e.g. initial login or switching users)
                setLoading(true);
                currentUserRoleFetchId = session.user.id;
                fetchUserRole(session.user.id);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const fetchUserRole = async (userId) => {
        try {
            const { data, error } = await supabase
                .from('user_roles')
                .select('role, cafe_id')
                .eq('user_id', userId)
                .maybeSingle();

            if (error) {
                if (error.code !== 'PGRST116') {
                    console.error("Error fetching user role:", error);
                }
                return;
            }

            if (data) {
                setUserRole(data.role);
                setUserCafeId(data.cafe_id);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <PageLoader theme={splashTheme} />;
    }

    return (
        <QueryClientProvider client={queryClient}>
            <PWAProvider>
                <LanguageProvider>
                <BrowserRouter>
                    <Suspense fallback={<PageLoader theme={splashTheme} />}>
                        <Routes>
                            <Route path="/login" element={!session ? <LoginPage /> : <Navigate to="/admin" />} />
                            <Route path="/reset-password" element={<LoginPage forcedTab="reset" />} />
                            <Route path="/admin" element={
                                !session ? <Navigate to="/login" /> : (
                                    userRole === 'super_admin' ? (
                                        <SuperAdminPanel />
                                    ) : userRole ? (
                                        <OwnerPanel cafeId={userCafeId} />
                                    ) : (
                                        <Navigate to="/" />
                                    )
                                )
                            } />

                            <Route path="/waiter/:cafeId" element={<WaiterApp />} />

                            {/* Subdomain Routing with Redirects for redundancy */}
                            {subdomainSlug ? (
                                <>
                                    <Route path="/" element={<CafeCover forcedSlug={subdomainSlug} />} />
                                    <Route path="/menu" element={<GuestMenu forcedSlug={subdomainSlug} />} />
                                    <Route path="/yml" element={<YmlFeed forcedSlug={subdomainSlug} />} />

                                    {/* Redirect redundant slug routes to clean subdomain routes */}
                                    <Route path="/:slug" element={<Navigate to="/" replace />} />
                                    <Route path="/:slug/menu" element={<Navigate to="/menu" replace />} />
                                    <Route path="/yml/:slug" element={<Navigate to="/yml" replace />} />
                                </>
                            ) : (
                                <>
                                    {/* Standard Routing (no subdomain) */}
                                    <Route path="/:slug" element={<CafeCover />} />
                                    <Route path="/:slug/menu" element={<GuestMenu />} />
                                    <Route path="/yml/:slug" element={<YmlFeed />} />
                                </>
                            )}

                            {/* LandingPage only on root if no subdomain */}
                            {!subdomainSlug && (
                                <>
                                    <Route path="/" element={<LandingPage />} />
                                </>
                            )}

                            {/* Legal Pages */}
                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                            <Route path="/public-offer" element={<PublicOffer />} />
                            <Route path="/user-agreement" element={<UserAgreement />} />
                        </Routes>
                    </Suspense>
                </BrowserRouter>
                <Analytics />
                <SpeedInsights />
                <CookieConsent />
            </LanguageProvider>
            </PWAProvider>
        </QueryClientProvider>
    );
}

export default App;